import { useState } from "react";
import { ChevronLeft, ChevronRight, MoreHorizontal, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ICalendarEvent } from "@/interfaces/dashboard.interface";
import { cn } from "@/lib/class-control";

interface CalendarWidgetProps {
  events: ICalendarEvent[];
  selectedDate?: Date;
  onDateSelect?: (date: Date) => void;
  onAddEventClick?: () => void;
}

export default function CalendarWidget({ events, selectedDate, onDateSelect, onAddEventClick }: CalendarWidgetProps) {
  const [currentDate, setCurrentDate] = useState(selectedDate || new Date());

  const monthNames = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
  ];

  const weekDays = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1; // Monday = 0

    const days = [];

    // Previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        day: prevMonthLastDay - i,
        isCurrentMonth: false,
        date: new Date(year, month - 1, prevMonthLastDay - i)
      });
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        day,
        isCurrentMonth: true,
        date: new Date(year, month, day)
      });
    }

    // Next month days
    const remainingDays = 42 - days.length; // 6 weeks * 7 days
    for (let day = 1; day <= remainingDays; day++) {
      days.push({
        day,
        isCurrentMonth: false,
        date: new Date(year, month + 1, day)
      });
    }

    return days;
  };

  const days = getDaysInMonth(currentDate);

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const isDateSelected = (date: Date) => {
    if (!selectedDate) return false;
    return (
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear()
    );
  };

  const formatEventDate = (date: string, startTime: string, endTime: string) => {
    const eventDate = new Date(date);
    const day = eventDate.getDate();
    const month = monthNames[eventDate.getMonth()];
    const year = eventDate.getFullYear();
    return `${day} de ${month}, ${year} - ${startTime} - ${endTime}`;
  };

  return (
    <div className="bg-[#15203C] rounded-[20px] p-[30px] flex flex-col gap-[40px]">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-[18px] font-bold text-white">Calendário de Tarefas</h2>
        <Button
          onClick={onAddEventClick}
          className="bg-[#7846FF] text-white hover:bg-[#6635E6] h-[50px] w-[205px] gap-[3px] text-[18px] font-bold"
        >
          <Plus className="size-6" />
          Add Novo evento
        </Button>
      </div>

      {/* Calendar */}
      <div className="flex flex-col gap-[20px]">
        {/* Month Navigation */}
        <div className="flex items-center justify-center gap-[183px]">
          <button
            onClick={handlePrevMonth}
            className="size-10 rounded-[20px] flex items-center justify-center hover:bg-white/10 transition-colors"
          >
            <ChevronLeft className="size-5 text-white" />
          </button>
          <p className="text-[16px] font-bold text-white">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </p>
          <button
            onClick={handleNextMonth}
            className="size-10 rounded-[20px] flex items-center justify-center hover:bg-white/10 transition-colors"
          >
            <ChevronRight className="size-5 text-white" />
          </button>
        </div>

        {/* Week Days */}
        <div className="grid grid-cols-7 gap-[43px] text-center">
          {weekDays.map((day) => (
            <p key={day} className="text-[14px] font-bold text-white">
              {day}
            </p>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-y-[20px] gap-x-[43px]">
          {days.map((dayInfo, index) => (
            <button
              key={index}
              onClick={() => onDateSelect?.(dayInfo.date)}
              className={cn(
                "size-[39px] rounded-[20px] flex items-center justify-center text-[14px] text-center transition-colors",
                dayInfo.isCurrentMonth ? "font-semibold text-white" : "font-normal text-white/50",
                isDateSelected(dayInfo.date) && "bg-[#7846FF]",
                !isDateSelected(dayInfo.date) && "hover:bg-white/10"
              )}
            >
              {dayInfo.day}
            </button>
          ))}
        </div>
      </div>

      {/* Divider */}
      <div className="border-b border-gray-800/60" />

      {/* Upcoming Events */}
      <div className="flex flex-col gap-[20px]">
        <div className="flex items-center justify-between">
          <p className="text-[14px] font-bold text-white">Próximos Eventos:</p>
          <button className="hover:opacity-70 transition-opacity">
            <MoreHorizontal className="size-6 text-white" />
          </button>
        </div>

        {/* Events List */}
        <div className="flex flex-col gap-[20px]">
          {events.length === 0 ? (
            <p className="text-center text-gray-400 text-sm py-4">
              Nenhum evento agendado
            </p>
          ) : (
            events.slice(0, 2).map((event, index) => (
              <div key={event.id} className="flex flex-col gap-[10px]">
                <div className="flex items-center gap-[10px]">
                  <div className="size-[7px] rounded-full bg-[#7846FF] flex-shrink-0" />
                  <p className="text-[14px] font-bold text-white">{event.title}</p>
                </div>
                <p className="text-[12px] text-white">
                  <span className="font-bold text-[#A085FF]">
                    {formatEventDate(event.date, event.startTime, event.endTime).split(" - ")[0]}
                  </span>
                  {" - " + event.startTime + " - " + event.endTime}
                </p>
                {index < events.length - 1 && index < 1 && (
                  <div className="border-b border-gray-800/60 mt-[10px]" />
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
